<template>
  <div>
    <div v-if="loading" class="flex flex-col items-center justify-center py-24 text-slate-400">
      <div class="h-8 w-8 rounded-full border-2 border-slate-200 border-t-brand animate-spin"></div>
      <p class="mt-3 text-sm">Yükleniyor…</p>
    </div>

    <div v-else-if="notFound" class="text-center py-24">
      <div class="text-5xl mb-3">🔍</div>
      <p class="text-slate-500">Kampanya bulunamadı.</p>
    </div>

    <template v-else>
      <!-- Brief -->
      <section class="rounded-3xl bg-white shadow-sm border border-slate-100 overflow-hidden">
        <div v-if="campaign.briefMediaUrl" class="bg-slate-900">
          <video v-if="isVideo(campaign.briefMediaUrl)" :src="campaign.briefMediaUrl" controls class="w-full max-h-96 object-contain"></video>
          <img v-else :src="campaign.briefMediaUrl" class="w-full max-h-96 object-contain" />
        </div>
        <div class="p-6 sm:p-8">
          <p class="text-xs font-medium text-brand uppercase tracking-wide">İçerik Kampanyası</p>
          <h1 class="text-2xl font-bold text-slate-800 mt-1">{{ campaign.name }}</h1>
          <div class="mt-4 brief-content text-slate-600 leading-relaxed" v-html="campaign.briefText"></div>

          <a v-if="campaign.briefDocumentUrl" :href="campaign.briefDocumentUrl" target="_blank"
             class="mt-5 flex items-center gap-3 rounded-xl border border-slate-200 hover:border-brand/40 hover:bg-slate-50 p-3 transition no-underline">
            <span class="rounded-lg bg-red-50 text-red-500 flex items-center justify-center" style="width:40px;height:40px;flex:none;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
            </span>
            <span class="flex-1 min-w-0 leading-tight">
              <span class="block text-sm font-medium text-slate-800">Kampanya dökümanı</span>
              <span class="block text-xs text-slate-400">PDF · görüntülemek için dokun</span>
            </span>
            <span class="rounded-lg bg-brand text-white text-xs font-semibold px-3 py-2" style="flex:none;">Aç</span>
          </a>
        </div>
      </section>

      <!-- Kayıt formu -->
      <section v-if="!token" class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-8">
        <h2 class="text-lg font-bold text-slate-800">Kampanyaya katıl</h2>
        <p class="text-sm text-slate-500 mt-1">Bilgilerini gir, hemen ilk içeriğini yükleyelim.</p>

        <div class="mt-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1.5">Instagram profil linki</label>
            <input v-model="reg.instagramHandle" type="url" inputmode="url" placeholder="https://instagram.com/kullaniciadi"
                   class="w-full rounded-xl border border-slate-200 py-3 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
            <p v-if="reg.instagramHandle && !isInstagramUrl(reg.instagramHandle)" class="text-xs text-red-500 mt-1.5">
              Geçerli bir Instagram profil linki gir (örn. https://instagram.com/kullaniciadi).
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1.5">E-posta</label>
            <input v-model="reg.email" type="email" placeholder="mail@ornek.com" class="w-full rounded-xl border border-slate-200 py-3 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
          </div>
          <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
          <button :disabled="busy || !canRegister" @click="register" class="w-full rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-40 text-white font-semibold py-3.5 transition shadow-sm shadow-brand/30">
            {{ busy ? "Kaydediliyor…" : "Devam et →" }}
          </button>
        </div>
      </section>

      <!-- Taslak yükleme -->
      <section v-else class="mt-5 rounded-3xl bg-white shadow-sm border border-slate-100 p-6 sm:p-8">
        <div class="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium bg-emerald-50 rounded-full px-3 py-1">
          <span>✓</span> Kayıt tamamlandı
        </div>

        <template v-if="!submitted">
          <h2 class="text-lg font-bold text-slate-800 mt-4">İlk içeriğini yükle</h2>
          <p class="text-sm text-slate-500 mt-1">Hazırladığın görsel veya videoyu yükle; ekibimiz inceleyip onaylayacak.</p>
          <p class="text-base font-bold text-red-600 mt-3 leading-snug">⚠️ Dikkat: "GEMINI" kelimesini büyük harflerle yazarken Türkçedeki "İ" harfini değil, İngilizce karakter olan "I" harfini kullanınız. Kelimenin doğru büyük harfli yazımı GEMINI şeklindedir.</p>

          <div v-if="campaign.requiredHashtags || campaign.requiredMentions || campaign.requiredUrl"
               class="mt-5 rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden">
            <div class="px-3.5 pt-3 pb-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">İçeriğinde kullan</div>
            <div v-if="campaign.requiredHashtags" class="flex items-start gap-3 p-3.5">
              <span class="text-base">#️⃣</span>
              <div><p class="text-xs text-slate-400 font-medium">Hashtag</p><p class="text-sm text-slate-700">{{ campaign.requiredHashtags }}</p></div>
              <button class="ml-auto text-xs text-brand font-medium" @click="copy(campaign.requiredHashtags)">Kopyala</button>
            </div>
            <div v-if="campaign.requiredMentions" class="flex items-start gap-3 p-3.5">
              <span class="text-base">@</span>
              <div><p class="text-xs text-slate-400 font-medium">Mention</p><p class="text-sm text-slate-700">{{ campaign.requiredMentions }}</p></div>
              <button class="ml-auto text-xs text-brand font-medium" @click="copy(campaign.requiredMentions)">Kopyala</button>
            </div>
            <div v-if="campaign.requiredUrl" class="flex items-start gap-3 p-3.5">
              <span class="text-base">🔗</span>
              <div class="min-w-0"><p class="text-xs text-slate-400 font-medium">Link</p><p class="text-sm text-brand break-all">{{ campaign.requiredUrl }}</p></div>
              <button class="ml-auto text-xs text-brand font-medium shrink-0" @click="copy(campaign.requiredUrl)">Kopyala</button>
            </div>
          </div>

          <div v-if="campaign.draftUrlLabel" class="mt-5">
            <label class="block text-sm font-medium text-slate-700 mb-1.5">{{ campaign.draftUrlLabel }}</label>
            <input v-model="draftUrl" type="url" :placeholder="campaign.draftUrlLabel" class="w-full rounded-xl border border-slate-200 py-3 px-3.5 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition" />
          </div>

          <div class="mt-5">
            <file-upload label="Dosya seç (resim / video)" accept="image/*,video/*" :busy="busy" :disabled="!!campaign.draftUrlLabel && !draftUrl" @submit="submitDraft" />
            <p v-if="error" class="text-sm text-red-500 mt-3">{{ error }}</p>
          </div>
        </template>

        <div v-else class="text-center py-10">
          <div class="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center text-3xl">🎉</div>
          <p class="font-semibold text-slate-800 mt-4 text-lg">İçeriğin alındı!</p>
          <p class="text-sm text-slate-500 mt-1.5">Onaylandığında e-posta ile bilgilendirileceksin.</p>
        </div>
      </section>

      <p v-if="copied" class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-sm px-4 py-2 rounded-full shadow-lg">Kopyalandı ✓</p>
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from "vue-property-decorator";
import api from "./api";
import FileUpload from "./file-upload.vue";

@Component({ components: { FileUpload } })
export default class CampaignPortal extends Vue {
  loading = true;
  notFound = false;
  busy = false;
  submitted = false;
  copied = false;
  error = "";
  campaign: any = {};
  token = "";
  draftUrl = "";
  reg = { instagramHandle: "", email: "" };

  get slug(): string {
    return this.$route.params.slug;
  }

  get canRegister(): boolean {
    return this.isInstagramUrl(this.reg.instagramHandle) && /\S+@\S+\.\S+/.test(this.reg.email);
  }

  isInstagramUrl(value: string): boolean {
    return /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9._]+\/?(\?.*)?$/i.test((value || "").trim());
  }

  async mounted() {
    try {
      this.campaign = await api.getJson(`/api/ContentCampaigns/public/${this.slug}`);
    } catch {
      this.notFound = true;
    } finally {
      this.loading = false;
    }
  }

  isVideo(url: string): boolean {
    return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  }

  copy(text: string) {
    navigator.clipboard.writeText(text);
    this.copied = true;
    setTimeout(() => (this.copied = false), 1500);
  }

  async register() {
    this.error = "";
    if (!this.isInstagramUrl(this.reg.instagramHandle)) {
      this.error = "Lütfen geçerli bir Instagram profil linki gir.";
      return;
    }
    this.busy = true;
    try {
      const payload = { ...this.reg, instagramHandle: this.reg.instagramHandle.trim() };
      const res = await api.postJson(`/api/ContentCampaigns/public/${this.slug}/register`, payload);
      this.token = res.token;
    } catch {
      this.error = "Kayıt sırasında bir hata oluştu. Tekrar dene.";
    } finally {
      this.busy = false;
    }
  }

  async submitDraft(file: File) {
    this.error = "";
    this.busy = true;
    try {
      const form = new FormData();
      form.append("type", "0");
      if (this.draftUrl) form.append("draftUrl", this.draftUrl);
      form.append("file", file);
      await api.postForm(`/api/ContentCampaigns/portal/${this.token}/submit`, form);
      this.submitted = true;
    } catch {
      this.error = "Yükleme başarısız. Tekrar dene.";
    } finally {
      this.busy = false;
    }
  }
}
</script>

<style>
.brief-content { word-break: break-word; }
.brief-content h1 { font-size: 1.5rem; font-weight: 700; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content h2 { font-size: 1.25rem; font-weight: 700; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.6em 0 0.3em; color: #1e293b; }
.brief-content p { margin: 0 0 0.6em; }
.brief-content ul { list-style: disc; padding-left: 1.4em; margin: 0 0 0.6em; }
.brief-content ol { list-style: decimal; padding-left: 1.4em; margin: 0 0 0.6em; }
.brief-content a { color: #6d28d9; text-decoration: underline; }
.brief-content strong { font-weight: 700; }
.brief-content u { text-decoration: underline; }
</style>
